import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { createTask, createTasksBulk, getUsers, getClients, getLeads, getClientMappedEmployees } from '../../lib/api';
import { formatApiError, getClientDisplayName } from '../../lib/utils';
import { PRIORITIES, AMC_NAMES, FOLIO_EXEMPT_TASK_TYPES, TASK_CATEGORY_MAP, TIME_SLOTS, PURCHASE_SUB_TYPES, BOND_TYPES, getTaskCategory } from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { DatePicker } from '../../components/ui/date-picker';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash, Users, UsersFour } from '@phosphor-icons/react';

const emptyTask = () => ({
  task_type: '',
  due_date: '',
  due_time: '',
  priority: 'Medium',
  description: '',
  assigned_to: '',
  folio_number: '',
  folio_type: 'existing',
  pan_number: '',
  kyc_status: '',
  amc_name: '',
  purchase_sub_type: 'Mutual Fund',
  bond_type: '',
  amount: '',
  amount_type: 'amount',
  units: '',
  attachment: null
});

export default function TaskFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forType = searchParams.get('for') || 'client';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [targetType, setTargetType] = useState(forType);
  const [targetId, setTargetId] = useState('');
  const [tasks, setTasks] = useState([emptyTask()]);
  const [mappedEmployees, setMappedEmployees] = useState([]);
  const [taskTypes, setTaskTypes] = useState(null); // admin-managed types from /settings
  const [amcNames, setAmcNames] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, clientsRes, leadsRes, taskTypesRes, amcRes] = await Promise.all([
          getUsers(),
          getClients(),
          getLeads(),
          api.get('/settings/task_types').catch(() => ({ data: { items: [] } })),
          api.get('/settings/amc_names').catch(() => ({ data: { items: [] } })),
        ]);
        setUsers(usersRes.data);
        setClients(clientsRes.data);
        setLeads(Array.isArray(leadsRes.data) ? leadsRes.data : leadsRes.data || []);
        const activeTypes = (taskTypesRes.data.items || []).filter(i => i.is_active).map(i => i.value);
        setTaskTypes(activeTypes.length > 0 ? activeTypes : null);
        setAmcNames((amcRes.data.items || []).filter(i => i.is_active).map(i => i.value));
      } catch (error) {
        toast.error(formatApiError(error));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only data loader

  // Fetch mapped employees when client is selected
  useEffect(() => {
    if (targetType === 'client' && targetId) {
      getClientMappedEmployees(targetId).then(res => setMappedEmployees(res.data)).catch(() => setMappedEmployees([]));
    } else {
      setMappedEmployees([]);
    }
  }, [targetType, targetId]);

  const updateTask = (index, field, value) => {
    setTasks(prev => prev.map((t, i) => {
      if (i !== index) return t;
      const updated = { ...t, [field]: value };
      if (field === 'task_type') {
        if (value !== 'Purchase') {
          updated.purchase_sub_type = 'Mutual Fund';
          updated.bond_type = '';
        }
        updated.folio_type = 'existing';
        updated.folio_number = '';
        updated.pan_number = '';
        updated.amount_type = 'amount';
        updated.amount = '';
        updated.units = '';
      }
      if (field === 'folio_type' && value === 'existing') {
        updated.pan_number = '';
      }
      if (field === 'amount_type') {
        // Clear the value that doesn't apply to the newly-selected mode.
        if (value === 'amount') updated.units = '';
        else if (value === 'units') updated.amount = '';
        else if (value === 'all_units') { updated.amount = ''; updated.units = ''; }
      }
      return updated;
    }));
  };

  const addTask = () => setTasks(prev => [...prev, emptyTask()]);
  const removeTask = (index) => {
    if (tasks.length === 1) return;
    setTasks(prev => prev.filter((_, i) => i !== index));
  };

  const needsFolio = (taskType) => taskType && !FOLIO_EXEMPT_TASK_TYPES.includes(taskType);
  const asksFolioType = (taskType) => taskType === 'SIP Start' || taskType === 'Purchase';
  const needsAmount = (taskType) => {
    const cat = getTaskCategory(taskType);
    return cat === 'SIP' || cat === 'FT';
  };

  const taskTypeGroups = (() => {
    // If admin has customised task types via /settings, group them into NFT/FT/SIP
    // using the constants map; unknown ones fall under "Other".
    if (!taskTypes || taskTypes.length === 0) {
      return Object.entries(TASK_CATEGORY_MAP).map(([cat, types]) => ({ label: cat, options: types }));
    }
    const buckets = { NFT: [], FT: [], SIP: [], Other: [] };
    for (const t of taskTypes) {
      const cat = getTaskCategory(t);
      // Only bucket by static map; if not present in any map value, treat as Other.
      const found = Object.entries(TASK_CATEGORY_MAP).find(([, list]) => list.includes(t));
      buckets[found ? found[0] : (cat && buckets[cat] ? cat : 'Other')].push(t);
    }
    return Object.entries(buckets)
      .filter(([, list]) => list.length > 0)
      .map(([label, options]) => ({ label, options }));
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!targetId) { toast.error(`Please select a ${targetType}`); return; }
    for (const t of tasks) {
      if (!t.task_type) { toast.error('Please select a task type'); return; }
      if (needsFolio(t.task_type) && !asksFolioType(t.task_type) && !t.folio_number) {
        toast.error(`Folio Number is required for ${t.task_type}`);
        return;
      }
      if (asksFolioType(t.task_type) && t.folio_type === 'existing' && !t.folio_number) {
        toast.error(`Folio Number is required for existing folio on ${t.task_type}`);
        return;
      }
      // For a NEW folio the PAN is mandatory (there is no folio yet).
      if (asksFolioType(t.task_type) && t.folio_type === 'new' && !t.pan_number) {
        toast.error(`PAN Number is required for a new folio on ${t.task_type}`);
        return;
      }
      if (t.task_type === 'KYC Updation' && !t.kyc_status) {
        toast.error('KYC Status is required for KYC Updation'); return;
      }
      // Amount validation depends on amount_type
      if (needsAmount(t.task_type)) {
        if (t.amount_type === 'amount' && (!t.amount || parseFloat(t.amount) <= 0)) {
          toast.error(`Amount is required for ${t.task_type}`); return;
        }
        if (t.amount_type === 'units' && (!t.units || parseFloat(t.units) <= 0)) {
          toast.error(`Units is required for ${t.task_type}`); return;
        }
      }
    }

    setSaving(true);
    try {
      const tasksPayload = tasks.map(t => ({
        ...t,
        client_id: targetType === 'client' ? targetId : undefined,
        lead_id: targetType === 'lead' ? targetId : undefined,
        folio_number: (t.folio_type === 'new') ? null : (t.folio_number || null),
        folio_type: t.folio_type || 'existing',
        pan_number: (t.folio_type === 'new') ? ((t.pan_number || '').toUpperCase() || null) : null,
        kyc_status: t.task_type === 'KYC Updation' ? (t.kyc_status || null) : null,
        amc_name: (t.task_type === 'Purchase' && t.purchase_sub_type === 'Bonds') ? t.bond_type : (t.amc_name || null),
        purchase_sub_type: t.task_type === 'Purchase' ? t.purchase_sub_type : null,
        amount_type: needsAmount(t.task_type) ? (t.amount_type || 'amount') : null,
        amount: needsAmount(t.task_type) && t.amount_type === 'amount' && t.amount ? parseFloat(t.amount) : null,
        units: needsAmount(t.task_type) && t.amount_type === 'units' && t.units ? parseFloat(t.units) : null,
        bond_type: undefined,
        attachment: undefined
      }));

      if (tasksPayload.length === 1) {
        await createTask(tasksPayload[0]);
      } else {
        await createTasksBulk(tasksPayload);
      }
      toast.success(`${tasksPayload.length} task(s) created successfully`);
      navigate('/tasks');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="p-6 space-y-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-[400px]" />
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="task-form-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/tasks')} className="p-2">
          <ArrowLeft size={20} />
        </Button>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          New Task{tasks.length > 1 ? 's' : ''}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="max-w-3xl space-y-6">
        <Card className="border-zinc-200 rounded-sm border-[#002FA7]/30 bg-blue-50/20">
          <CardContent className="p-6 space-y-4">
            <div className="flex gap-3">
              <Button
                type="button"
                variant={targetType === 'client' ? 'default' : 'outline'}
                className={targetType === 'client' ? 'bg-[#002FA7] text-white rounded-sm' : 'rounded-sm'}
                onClick={() => { setTargetType('client'); setTargetId(''); }}
                data-testid="task-target-client"
              >
                <Users size={16} className="mr-2" /> Client
              </Button>
              <Button
                type="button"
                variant={targetType === 'lead' ? 'default' : 'outline'}
                className={targetType === 'lead' ? 'bg-amber-600 text-white rounded-sm' : 'rounded-sm'}
                onClick={() => { setTargetType('lead'); setTargetId(''); }}
                data-testid="task-target-lead"
              >
                <UsersFour size={16} className="mr-2" /> Lead
              </Button>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                {targetType === 'client' ? 'Client' : 'Lead'} <span className="text-red-500">*</span>
              </Label>
              {targetType === 'client' ? (
                <SearchableSelect
                  value={targetId}
                  onValueChange={setTargetId}
                  options={clients.map(c => ({ value: c.id, label: getClientDisplayName(c) }))}
                  placeholder="Select client"
                  searchPlaceholder="Search clients..."
                  data-testid="task-client-select"
                />
              ) : (
                <SearchableSelect
                  value={targetId}
                  onValueChange={setTargetId}
                  options={leads.map(l => ({ value: l.id, label: `${l.full_name || l.name || 'Unnamed'} ${l.phone ? `(${l.phone})` : ''}` }))}
                  placeholder="Select lead"
                  searchPlaceholder="Search leads..."
                  data-testid="task-lead-select"
                />
              )}
            </div>
          </CardContent>
        </Card>

        {tasks.map((task, index) => (
          <Card key={index} className="border-zinc-200 rounded-sm" data-testid={`task-card-${index}`}>
            <CardHeader className="border-b border-zinc-100 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Task {tasks.length > 1 ? `#${index + 1}` : 'Details'}</CardTitle>
              {tasks.length > 1 && (
                <Button type="button" variant="ghost" size="sm" onClick={() => removeTask(index)} className="text-red-500 hover:text-red-700">
                  <Trash size={16} />
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Type <span className="text-red-500">*</span></Label>
                  <SearchableSelect
                    value={task.task_type}
                    onValueChange={(v) => updateTask(index, 'task_type', v)}
                    groups={taskTypeGroups}
                    placeholder="Select type"
                    data-testid={`task-type-${index}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Priority</Label>
                  <SearchableSelect
                    value={task.priority}
                    onValueChange={(v) => updateTask(index, 'priority', v)}
                    options={PRIORITIES}
                    placeholder="Select priority"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Due Date <span className="text-red-500">*</span></Label>
                  <DatePicker
                    value={task.due_date}
                    onChange={(v) => updateTask(index, 'due_date', v)}
                    placeholder="Select due date"
                    data-testid={`task-date-${index}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Due Time</Label>
                  <SearchableSelect
                    value={task.due_time || ''}
                    onValueChange={(v) => updateTask(index, 'due_time', v)}
                    options={TIME_SLOTS.map(s => ({ value: s.value, label: s.label }))}
                    placeholder="No time"
                    data-testid={`task-time-${index}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Assigned To</Label>
                  <SearchableSelect
                    value={task.assigned_to}
                    onValueChange={(v) => updateTask(index, 'assigned_to', v)}
                    options={(targetType === 'client' && mappedEmployees.length > 0 ? mappedEmployees : users.filter(u => u.status === 'active' || u.id)).map(u => ({ value: u.id, label: u.name }))}
                    placeholder="Select employee"
                    searchPlaceholder="Search employees..."
                    data-testid={`task-assigned-to-${index}`}
                  />
                </div>

                {task.task_type === 'Purchase' && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Purchase Type <span className="text-red-500">*</span></Label>
                    <SearchableSelect
                      value={task.purchase_sub_type}
                      onValueChange={(v) => { updateTask(index, 'purchase_sub_type', v); updateTask(index, 'amc_name', ''); updateTask(index, 'bond_type', ''); }}
                      options={PURCHASE_SUB_TYPES}
                      placeholder="Select purchase type"
                      data-testid={`task-purchase-sub-${index}`}
                    />
                  </div>
                )}

                {!(task.task_type === 'Purchase' && task.purchase_sub_type === 'Bonds') && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold">
                      AMC Name {needsFolio(task.task_type) && <span className="text-red-500">*</span>}
                    </Label>
                    <SearchableSelect
                      value={task.amc_name}
                      onValueChange={(v) => updateTask(index, 'amc_name', v)}
                      options={amcNames.length > 0 ? amcNames : AMC_NAMES}
                      placeholder="Select AMC"
                      searchPlaceholder="Search AMC..."
                      data-testid={`task-amc-${index}`}
                    />
                  </div>
                )}

                {task.task_type === 'Purchase' && task.purchase_sub_type === 'Bonds' && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Bond Type <span className="text-red-500">*</span></Label>
                    <SearchableSelect
                      value={task.bond_type}
                      onValueChange={(v) => updateTask(index, 'bond_type', v)}
                      options={BOND_TYPES}
                      placeholder="Select bond type"
                      data-testid={`task-bond-type-${index}`}
                    />
                  </div>
                )}

                {asksFolioType(task.task_type) && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Folio <span className="text-red-500">*</span></Label>
                    <SearchableSelect
                      value={task.folio_type}
                      onValueChange={(v) => { updateTask(index, 'folio_type', v); if (v === 'new') updateTask(index, 'folio_number', ''); }}
                      options={[{ value: 'new', label: 'New Folio' }, { value: 'existing', label: 'Existing Folio' }]}
                      placeholder="Select folio type"
                      data-testid={`task-folio-type-${index}`}
                    />
                  </div>
                )}

                {asksFolioType(task.task_type) && task.folio_type === 'existing' && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold">
                      Folio Number <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={task.folio_number}
                      onChange={(e) => updateTask(index, 'folio_number', e.target.value)}
                      required
                      className="rounded-sm"
                      placeholder="e.g., 12345678/90"
                      data-testid={`task-folio-${index}`}
                    />
                  </div>
                )}

                {asksFolioType(task.task_type) && task.folio_type === 'new' && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold">
                      PAN Number <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={task.pan_number}
                      onChange={(e) => updateTask(index, 'pan_number', e.target.value.toUpperCase())}
                      required
                      maxLength={10}
                      className="rounded-sm uppercase"
                      placeholder="e.g., ABCDE1234F"
                      data-testid={`task-pan-${index}`}
                    />
                    <p className="text-[11px] text-zinc-500">Required for a new folio since no folio number exists yet.</p>
                  </div>
                )}

                {!asksFolioType(task.task_type) && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold">
                    Folio Number {needsFolio(task.task_type) && <span className="text-red-500">*</span>}
                  </Label>
                  <Input
                    value={task.folio_number}
                    onChange={(e) => updateTask(index, 'folio_number', e.target.value)}
                    required={needsFolio(task.task_type)}
                    className="rounded-sm"
                    placeholder="e.g., 12345678/90"
                    data-testid={`task-folio-${index}`}
                  />
                </div>
                )}

                {task.task_type === 'KYC Updation' && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold">KYC Status <span className="text-red-500">*</span></Label>
                    <SearchableSelect
                      value={task.kyc_status}
                      onValueChange={(v) => updateTask(index, 'kyc_status', v)}
                      options={[
                        { value: 'Pending', label: 'Pending' },
                        { value: 'Under Process', label: 'Under Process' },
                        { value: 'Validated', label: 'Validated' },
                        { value: 'Registered', label: 'Registered' },
                      ]}
                      placeholder="Select KYC status"
                      data-testid={`task-kyc-status-${index}`}
                    />
                  </div>
                )}

                {needsAmount(task.task_type) && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Value Type <span className="text-red-500">*</span></Label>
                      <SearchableSelect
                        value={task.amount_type}
                        onValueChange={(v) => updateTask(index, 'amount_type', v)}
                        options={[
                          { value: 'amount', label: 'Amount (₹)' },
                          { value: 'units', label: 'Units' },
                          { value: 'all_units', label: 'All Units' },
                        ]}
                        placeholder="Select"
                        data-testid={`task-amount-type-${index}`}
                      />
                    </div>
                    {task.amount_type === 'amount' && (
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Amount (₹) <span className="text-red-500">*</span></Label>
                        <Input
                          type="number" min="0" step="0.01"
                          value={task.amount}
                          onChange={(e) => updateTask(index, 'amount', e.target.value)}
                          required className="rounded-sm" placeholder="e.g., 50000"
                          data-testid={`task-amount-${index}`}
                        />
                      </div>
                    )}
                    {task.amount_type === 'units' && (
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Units <span className="text-red-500">*</span></Label>
                        <Input
                          type="number" min="0" step="0.0001"
                          value={task.units}
                          onChange={(e) => updateTask(index, 'units', e.target.value)}
                          required className="rounded-sm" placeholder="e.g., 125.35"
                          data-testid={`task-units-${index}`}
                        />
                      </div>
                    )}
                    {task.amount_type === 'all_units' && (
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">All Units</Label>
                        <div className="rounded-sm border border-dashed border-zinc-300 px-3 py-2 text-xs text-zinc-500 bg-zinc-50">
                          The operation will apply to the entire holding on this folio — no value needed.
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold">Description</Label>
                <Textarea
                  value={task.description}
                  onChange={(e) => updateTask(index, 'description', e.target.value)}
                  rows={2} className="rounded-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Attachment (optional)</Label>
                <Input
                  type="file"
                  onChange={(e) => updateTask(index, 'attachment', e.target.files?.[0] || null)}
                  className="rounded-sm text-sm file:mr-3 file:rounded-sm file:border-0 file:bg-zinc-100 file:px-3 file:py-1 file:text-xs file:font-medium"
                  data-testid={`task-attachment-${index}`}
                />
                {task.attachment && <p className="text-xs text-zinc-400">{task.attachment.name}</p>}
              </div>
            </CardContent>
          </Card>
        ))}

        <Button type="button" variant="outline" onClick={addTask} className="w-full rounded-sm border-dashed" data-testid="add-another-task-button">
          <Plus size={16} className="mr-2" /> Add Another Task
        </Button>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/tasks')} className="rounded-sm">Cancel</Button>
          <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm" data-testid="task-submit-button">
            {saving ? 'Creating...' : `Create ${tasks.length} Task${tasks.length > 1 ? 's' : ''}`}
          </Button>
        </div>
      </form>
    </div>
  );
}
